import { Component, EventEmitter, Input, OnInit, Output, DestroyRef, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY_GUID } from '@core/constants';
import {
  AuthorizationCertPeriod,
  AuthorizationDetail,
  AuthorizationHCPCReqVisits,
  AuthorizationRequest,
  BusinessTypeIDs,
  CertPeriodRangeCalcDTO,
  ExistingServiceLineDetail,
  RetroactiveDateRangeCalc,
  ReviewPeriodType,
  ServiceLineGridDetail,
  ServiceLineGridPreview,
  ServiceLinesGridDetails
} from '@core/model';
import {
  AuthenticationService,
  AuthorizationRequestService,
  AuthorizationService,
  ProviderManagementService,
  TenantService
} from '@core/service';
import { RequestSources, ReviewPeriodTypes } from '@shared/enumeration';
import { ReviewPeriod } from '@shared/model';
import { BusinessType, ProviderAccountPgpDTO, SessionStorageService,FeatureToggleService, ProviderPgpDTO } from '@mynexus/mynexus-ui-lib';
import moment from 'moment';
import { ToastMessageOptions, MessageService, SelectItem } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { ReferenceByEntity } from '@core/model/reference-by-entity';
import { ReviewPeriodMessage } from '@core/constants/messages';
import { DatePipe } from '@angular/common';
import { DialogService } from 'primeng/dynamicdialog';
import { RequestVisitsDialogComponent } from '@shared/component/request-visits-dialog/request-visits-dialog.component';
import { DuplicateRequestVisitsDialogComponent } from '@shared/component/duplicate-request-visits-dialog/duplicate-request-visits-dialog.component';
import { AuthorizationDecisionService } from '@core/service/authorization-decision.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type RequestType = 'Subsequent' | 'Initial';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-auth-request-services',
    templateUrl: './auth-request-services.component.html',
    styleUrls: ['./auth-request-services.component.scss'],
    standalone: false
})
export class AuthRequestServicesComponent implements OnInit {
  @Output() isStepFormValid = new EventEmitter<boolean>();
  @Output() authRequestChanged = new EventEmitter<AuthorizationRequest>();
  @Output() serviceLinesChanged = new EventEmitter<ServiceLineGridDetail[]>();
  @Output() goToNextStep = new EventEmitter<boolean>();
  @Output() socWasConfirmed = new EventEmitter<boolean>();
  @Output() certPeriodQueue = new EventEmitter<AuthorizationCertPeriod>();
  @Input() requestType: RequestType;
  @Input() authRequest: AuthorizationRequest;
  @Input() selectedDetails: ServiceLineGridDetail[];
  @Input() serviceProvider: ProviderAccountPgpDTO;
  // format of this prop is - 'Wed, 29 May 2024 14:59:02 GMT', it may have issues with several pipes
  @Input() dateTimeFaxReceived: string;
  @Input() selectedCertPeriod: AuthorizationCertPeriod;
  @Input() contractStartDate: string;
  @Input() certPeriod?: AuthorizationCertPeriod;
  @Input() memberRepresentativeName: string;
  @Input() hh30DayReviewPeriods = false;
  @Input() isHHRequestProcessUpdates = false;
  @Input() retroExceptionDays: number | null;

  certPeriodModalVisible = false;
  tenantId: string;
  subscriberNumber: string;
  isExternalUser: boolean;
  isServiceLineDetailFormBuild = false;
  subsequentRequestType = 'Subsequent';
  initialRequestType = 'Initial';
  retroServiceType = 'Retro';
  routineUrgency = 'Routine';
  cancelled = 'Cancelled';
  nonAdmit = 'Non-Admit';
  emptyGuid = EMPTY_GUID;
  editHeaderText = 'Enter the following information and click the save button to complete the service line.';
  noRetroPeriodMessage = 'No Retro Period';
  zzHcpc = 'ZZ';
  homeHealth = 'Home Health Care';
  displayedEditHeaderText: string = this.editHeaderText;
  duplicateHeaderText = 'Duplicate Request';
  serviceLineDetailForm: FormGroup;
  serviceLineDetailGroup: FormGroup;
  socDateEntered: string = null;
  serviceLineDetails: ServiceLineGridDetail[] = [];
  editDetail: ServiceLineGridDetail = {};
  isGettingRetroDateRange = false;
  showPreview = false;
  serviceLinesPreview: ServiceLineGridPreview[] = [];
  displayServiceLineQuestion = false;
  displayHippsCodeDialog = false;
  showNA = true;
  requestedVisitsValid = true;
  isDisabled = false;
  originalAuthorizationDetailDTOs: AuthorizationDetail[];
  certPeriods: CertPeriodRangeCalcDTO[];
  firstCertPeriod: CertPeriodRangeCalcDTO;

  authorizationHCPCReqVisits: AuthorizationHCPCReqVisits[];

  isLoading = false;
  hasContractFlow: boolean;
  businessType: BusinessType = null;

  private destroyRef = inject(DestroyRef);

  displayRequestorDialog = false;
  requestorTypes: SelectItem[];
  displayMemberRepresentative = false;

  woundOstomyCriterias: ReferenceByEntity[] = [];

  showHeaderForDateSelect = false;
  canRequestNextReviewPeriod = false;

  readonly reviewPeriodMessage: ToastMessageOptions = {
    severity: 'warn',
    detail: ReviewPeriodMessage,
  };

  reviewPeriodOptions: SelectItem[] = [];
  reviewPeriods: ReviewPeriod[] = [];
  hideReviewPeriodField = true;
  reviewPeriodA: ReviewPeriod;
  reviewPeriodB: ReviewPeriod;
  newLineForNextPeriod: ServiceLineGridDetail;
  reviewPeriodException = false;
  selectedReviewPeriodEndDate: string;
  selectedReviewPeriodStartDate: string;
  preselectReviewPeriod: string;
  disableReviewPeriod = false;
  noRetroVisitsForCSD = false;

  private readonly serviceProviderBusinessGroup = [BusinessTypeIDs.HomeHealth, BusinessTypeIDs.AdminHomeHealth, BusinessTypeIDs.SDoH, BusinessTypeIDs.SDoHProviderAdmin];
  private readonly physicianBusinessGroup = [BusinessTypeIDs.Physician];
  private readonly healthPlanCaseManagerBusinessGroup = [BusinessTypeIDs.HealthPlanCaseManager];

  constructor(
    private authenticationService: AuthenticationService,
    private authorizationRequestService: AuthorizationRequestService,
    private authorizationDecisionService: AuthorizationDecisionService,
    private authorizationService: AuthorizationService,
    private messageService: MessageService,
    private sessionStorageService: SessionStorageService,
    private route: ActivatedRoute,
    private router: Router,
    private tenantService: TenantService,
    private datePipe: DatePipe,
    private dialogService: DialogService,
    private readonly featureService: FeatureToggleService,
    private readonly providerManagementService: ProviderManagementService

  ) { }

  ngOnInit() {
    // Get Global Values
    this.setGlobalVariables();

    // Get Lookups
    this.getLookups();

    // Set initial form validity
    this.setFormValidity();

    // Update selected services liens
    this.updateSelectedLineDetails();

    if (this.hh30DayReviewPeriods) {
      this.editHeaderText = 'Request Visits';
      this.displayedEditHeaderText = 'Request Visits';

      if (this.serviceProvider) {
        this.reviewPeriodException = this.serviceProvider.providerPgpDTO?.reviewPeriodException == 1
      }
    }
  }

  nextReviewPeriodRequest(event: { skill: string, reviewPeriod: ReviewPeriod }): void {
    const { skill, reviewPeriod } = event;
    const hCPCReqVisit = this.authorizationHCPCReqVisits.find(hCPCReqVisit => hCPCReqVisit.skillCode === skill);
    const insertIndex = this.selectedDetails.findIndex(serviceLine => serviceLine.skillCode === skill) + 1;

    this.newLineForNextPeriod = this.createServiceLineGridDetail(hCPCReqVisit.hcpcCode, hCPCReqVisit.skillCode, hCPCReqVisit.totalVisits, this.retroExceptionDays);
    this.selectedDetails.splice(insertIndex, 0, this.newLineForNextPeriod);
    this.requestVisitsForNextPeriod(this.newLineForNextPeriod, reviewPeriod.reviewPeriodType);
  }

  addDaysToDateString(dateString: string): string {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }


  private requestVisits(createNextReviewPeriodClicked = false): void {
    this.dialogService.open(RequestVisitsDialogComponent, {
      header: this.displayedEditHeaderText,
      width: '740px',
      closable: false,
      draggable: false,
      data: {
        requestType: this.requestType,
        tenantId: this.tenantId,
        isExternalUser: this.isExternalUser,
        editDetail: this.editDetail,
        showNA: this.showNA,
        disableReviewPeriod: this.disableReviewPeriod,
        selectedCertPeriod: this.selectedCertPeriod,
        dateTimeFaxReceived: this.dateTimeFaxReceived,
        startOfCareDate: this.authRequest.authorizationDTO.startOfCareDate,
        hasContractFlow: this.hasContractFlow,
        contractStartDate: this.contractStartDate,
        hideReviewPeriodField: this.hideReviewPeriodField,
        reviewPeriodOptions: this.reviewPeriodOptions,
        reviewPeriods: this.reviewPeriods,
        preselectReviewPeriod: this.preselectReviewPeriod,
        hh30DayReviewPeriods: this.hh30DayReviewPeriods,
        createNextReviewPeriodClicked: createNextReviewPeriodClicked,
        noRetroVisitsForCSD: this.noRetroVisitsForCSD,
        isHHRequestProcessUpdates: this.isHHRequestProcessUpdates,
        participationStatus: this.serviceProvider?.providerPgpDTO?.participationStatus,
        reimbursementType: this.serviceProvider?.providerPgpDTO?.reimbursementType,
        reviewPeriodException: this.reviewPeriodException,
      },
    }).onClose.subscribe(res => {
      if (res) {
        this.editDetail = res.editDetail;
        if (this.isExternalUser) {
          this.checkForDuplicateSL(res.selectedMemberCriteriasIds, res.reviewData);
        }
        else {
          this.handleEditDialogSave(res.selectedMemberCriteriasIds);
        }
      } else {
        this.handleEditDialogCancel();
      }

      if (this.hh30DayReviewPeriods) {
        if (this.isPreselectReviewPeriodA) {
          this.preselectReviewPeriod = this.reviewPeriodA.reviewPeriodType;
          this.hideReviewPeriodField = true;
        } else {
          this.preselectReviewPeriod = '';
        }

        this.disableReviewPeriod = false;
      }
    })
  }



  checkForDuplicateSL(selectedMemberCriteriasIds: string[], reviewData : ReviewPeriod) {
    var requestServiceLine : AuthorizationDetail = {
      reviewPeriodId : this.reviewPeriods.find(x => x.reviewPeriodType === reviewData.reviewPeriodType).reviewPeriodId,
      effectiveFromDate : reviewData.startDate,
      effectiveToDate : reviewData.endDate,
      hcpccode: this.editDetail.hcpcCode,
      authorizationDetailId : this.editDetail.authorizationDetailId,
      dateTimeFaxRecieved : this.editDetail.dateTimeFaxRecieved,
    }
    this.authorizationDecisionService.FetchDuplicateServiceLine(this.tenantId, this.route.snapshot.params.memberId, requestServiceLine)
      .subscribe(resp => {
        if (resp.skillCode != null) {
          this.dialogService.open(DuplicateRequestVisitsDialogComponent, {
            header: this.duplicateHeaderText,
            width: '640px',
            data: {
              requestType: this.requestType,
              tenantId: this.tenantId,
              isExternalUser: this.isExternalUser,
              editDetail: this.editDetail,
              deniedVisitDetails: resp,
              dateTimeFaxReceived: this.dateTimeFaxReceived,
            },
            closable: true,
          }).onClose.subscribe(res => {
            if (res) {
              this.editDetail.duplicateRequest = true;
              this.handleEditDialogSave(selectedMemberCriteriasIds);
            } else {
              this.handleEditDialogCancel();
            }
          })
        }
        else{
          this.handleEditDialogSave(selectedMemberCriteriasIds);
        }
      })
  }

  get isDTRRMoreThan7DaysToReviewPeriodB(): boolean {
    const dateTimeFaxReceived = this.initialRequestType === this.requestType ? this.authRequest.authorizationDTO.dateTimeFaxReceived : this.dateTimeFaxReceived;

    return this.reviewPeriodB
      ? new Date(dateTimeFaxReceived).getTime() < (new Date(this.reviewPeriodB.startDate).getTime() - (7 * 24 * 60 * 60 * 1000))
      : false;
  }

  get isPreselectReviewPeriodA() {
    let shouldPreselectReviewPeriodA = (!this.reviewPeriodException && (this.isDTRRMoreThan7DaysToReviewPeriodB || !this.reviewPeriodB)) || (this.initialRequestType === this.requestType && this.hasContractFlow && this.certPeriods.length === 1)
    if(this.certPeriods != null){
      shouldPreselectReviewPeriodA = shouldPreselectReviewPeriodA && this.certPeriods.length <= 1;
    }
    return shouldPreselectReviewPeriodA;
  }

  private requestVisitsForNextPeriod(data: ServiceLineGridDetail, reviewPeriodType: string): void {
    this.hideReviewPeriodField = false;
    this.preselectReviewPeriod = reviewPeriodType;
    this.disableReviewPeriod = true;
    this.editDetail = data;
    this.showNA = false;
    this.requestType === this.initialRequestType;
    this.editDetail.dateLastVisitDeliveredLabel = 'When will the very last visit, from the previously approved visit(s) be completed?';
    this.displayedEditHeaderText = this.editHeaderText;
    this.requestVisits(true);
  }

  private initReviewPeriods(): void {
    // Grab Review Periods
    this.reviewPeriods = this.selectedCertPeriod.reviewPeriods;

    // Assign Review Period Options
    this.manageReviewPeriodOptions();

    // Show Review Period Field
    this.hideReviewPeriodField = false;
    this.canRequestNextReviewPeriod = !(this.initialRequestType === this.requestType && this.hasContractFlow) && !!this.reviewPeriodB && (!this.isDTRRMoreThan7DaysToReviewPeriodB || this.reviewPeriodException);
  }

  private manageReviewPeriodOptions(): void {
    this.reviewPeriodOptions = [];
    this.reviewPeriods.forEach(x => {
      const startDate = this.datePipe.transform(x.startDate, 'MM/dd/yyyy');
      const endDate = this.datePipe.transform(x.endDate, 'MM/dd/yyyy');

      this.reviewPeriodA = x.reviewPeriodType === ReviewPeriodTypes.PeriodA ? x : this.reviewPeriodA;
      this.reviewPeriodB = x.reviewPeriodType === ReviewPeriodTypes.PeriodB ? x : this.reviewPeriodB;
      this.reviewPeriodOptions.push(
        {
          label: `${x.reviewPeriodType === ReviewPeriodTypes.PeriodA ? 'A' : 'B'} - ${startDate} - ${endDate}`,
          value: x.reviewPeriodType
        },
      )
    });
  }

  handleHippsCode(hippsCode) {
    if (hippsCode) {
      this.selectedCertPeriod.hippscode = hippsCode;
      this.authRequest.authorizationCertPeriodDTOs.find(e => e.certPeriod === this.selectedCertPeriod.certPeriod).hippscode = hippsCode;
      this.authRequestChanged.emit(this.authRequest);
    }

    this.displayHippsCodeDialog = false;
    this.handleHippsDialogContinue();
  }

  setGlobalVariables() {
    this.tenantId = this.route.snapshot.params.tenantId;
    this.subscriberNumber = this.route.snapshot.params.memberId;
    this.isExternalUser = this.authenticationService.isExternalUser;
    this.hasContractFlow = this.checkContractStartDate();
    this.getBusinessType();

    if (!this.authRequest.authorizationDetailDTOs) {
      this.authRequest.authorizationDetailDTOs = [];
    }
    this.originalAuthorizationDetailDTOs = this.authRequest.authorizationDetailDTOs.map(authorizationDetailDTO => ({ ...authorizationDetailDTO }));
  }

  getLookups() {
    if (this.serviceLineDetails && this.serviceLineDetails.length === 0) {
      this.getAuthorizationHCPCReqVisits();
    }
  }

  getAuthorizationHCPCReqVisits() {
    this.isLoading = true;
    this.authorizationService.
      getHcpcReqVisitsByAuthorizationID(this.tenantId, this.authRequest.authorizationDTO.authorizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(resp => {
        this.authorizationHCPCReqVisits = resp;
        this.populateServiceLineGridDetail(resp);
        // Build Forms
        this.buildForms();
        // Show question modal
        this.showInitialQuestions();
        this.isLoading = false;
      });
  }

  private generateHiddenUID(): string {
    return 'uid-' + Date.now() + Math.random().toString();
  }

  private createServiceLineGridDetail(hcpcCode: string, skillCode: string, totalVisits: number, retroDays: number): ServiceLineGridDetail {
    return {
      certFromDate: this.datePipe.transform(this.selectedCertPeriod.effectiveFromDate, "MM/dd/yyyy"),
      certToDate: this.datePipe.transform(this.selectedCertPeriod.effectiveToDate, "MM/dd/yyyy"),
      certPeriod: this.selectedCertPeriod.certPeriod,
      dateTimeFaxRecieved: this.dateTimeFaxReceived,
      reCertDate: this.authRequest.authorizationDTO.recertDate === null ? null : this.authRequest.authorizationDTO.recertDate,
      startOfCareDate: this.authRequest.authorizationDTO.startOfCareDate,
      totalPreviouslyApprovedVisits: totalVisits,
      retroDays,
      skillCode,
      hcpcCode,
      reviewPeriodType: null,
      hiddenUID: this.generateHiddenUID(),
      additionalComments: null,
      ...(this.hasContractFlow && { contractStartDate: this.contractStartDate }),
      ...(this.requestType === this.initialRequestType && { urgency: this.authRequest.authorizationDTO.urgency }),
    };
  }

  populateServiceLineGridDetail(hcpcs: AuthorizationHCPCReqVisits[]): void {
    hcpcs.forEach(({ hcpcCode, skillCode, totalVisits }) => {
      if (this.selectedDetails?.find(selectedDetail => selectedDetail?.hcpcCode === hcpcCode)) {

        this.selectedDetails.forEach(selectedDetail => {
          if (selectedDetail.hcpcCode === hcpcCode) {
            this.serviceLineDetails.push({
              ...selectedDetail,
              dateTimeFaxRecieved: this.dateTimeFaxReceived,
              ...(this.requestType === this.initialRequestType && { urgency: this.authRequest.authorizationDTO.urgency })
            });
          }
        })
      } else {
        this.serviceLineDetails.push(this.createServiceLineGridDetail(hcpcCode, skillCode, totalVisits, this.retroExceptionDays));
      }});
  }

  buildForms() {
    this.buildServiceLinesDetailForm();

    if (this.hh30DayReviewPeriods) {
      this.initReviewPeriods();
    }
  }

  //TODO check if we need it
  buildServiceLinesDetailForm() {
    this.serviceLineDetailForm = new FormGroup({
      serviceLines: new FormControl(new FormArray([]))
    });
    const details = new FormArray([]);
    this.serviceLineDetails.forEach(sl => {
      this.serviceLineDetailGroup = this.createServiceLineDetailFormGroup(sl);
      details.push(this.serviceLineDetailGroup);
    });

    this.serviceLineDetailForm.controls['serviceLines'] = details;
    this.isServiceLineDetailFormBuild = true;
  }

  createServiceLineDetailFormGroup(sl: ServiceLineGridDetail): FormGroup {
    return new FormGroup({
      dateTimeFaxReceived: new FormControl(sl.dateTimeFaxRecieved),
      urgency: new FormControl(sl.urgency),
      hcpcCode: new FormControl(sl.hcpcCode),
      previouslyAllowedVisits: new FormControl(sl.totalPreviouslyApprovedVisits),
      dateLastVisitDelivered: new FormControl(sl.dateLastVisitDelivered),
      retroDateRange: new FormControl(''),
      retroVisitsRequested: new FormControl(sl.retroVisitsRequested, [Validators.min(0), Validators.max(999), Validators.pattern('[0-9]*')]),
      timelyVisitsRequested: new FormControl(sl.timelyVisitsRequested, [Validators.min(0), Validators.max(9999), Validators.pattern('[0-9]*')]),
      totalVisitsRequested: new FormControl(sl.totalVisitsRequested),
      reviewPeriodType: new FormControl(''),
      additionalComments: new FormControl(sl.additionalComments)
    });
  }

  private findLine(serviceLineDetails: ServiceLineGridDetail[], hcpcCode: string, reviewPeriodType: string): ServiceLineGridDetail {
    return serviceLineDetails.find(serviceLine => serviceLine.hcpcCode === hcpcCode
      && (this.hh30DayReviewPeriods ? serviceLine?.reviewPeriodType === reviewPeriodType : true));
  }

  handleDetailRowChecked($event) {
    this.editDetail = $event.data;

    // 19265 Clearout Previous SL values
    const serviceLineDetail = this.findLine(this.serviceLineDetails, this.editDetail.hcpcCode, this.editDetail?.reviewPeriodType);

    if (serviceLineDetail) {
      serviceLineDetail.timelyVisitsRequested = null;
      serviceLineDetail.totalVisitsRequested = null;
    }

    this.showNA = this.authRequest.authorizationDetailDTOs
      .findIndex(e => e.hcpccode === this.editDetail.hcpcCode &&
        e.authorizationDetailId !== this.emptyGuid &&
        e.serviceStatus !== this.cancelled &&
        e.serviceStatus !== this.nonAdmit) === -1 ||
      this.requestType === this.initialRequestType;
    this.editDetail.dateLastVisitDeliveredLabel = this.showNA ? 'Date of Discipline\'s First Visit' : 'When will the very last visit, from the previously approved visit(s) be completed?';
    this.displayedEditHeaderText = this.editHeaderText;

    this.requestVisits();
  }

  private filterLines(lines: ServiceLineGridDetail[], hcpcCode: string, reviewPeriodType: string): ServiceLineGridDetail[] {
    return lines.filter(serviceLine => !(serviceLine.hcpcCode === hcpcCode && serviceLine.reviewPeriodType === reviewPeriodType));
  }

  private unCheckRowForNextPeriod(): void {
    if (!this.hh30DayReviewPeriods) {
      return;
    }

    const isDoubledLine = this.serviceLineDetails.filter(serviceLineDetail => serviceLineDetail.hcpcCode === this.editDetail.hcpcCode).length > 1;

    if (isDoubledLine && this.editDetail?.reviewPeriodType === this.reviewPeriodA.reviewPeriodType) {
      const index = this.selectedDetails.findIndex(selectDetail => selectDetail.hcpcCode === this.editDetail.hcpcCode);

      if (index > -1) {
        this.serviceLineDetails = this.filterLines(this.serviceLineDetails, this.editDetail.hcpcCode, this.selectedDetails[index].reviewPeriodType);
        this.selectedDetails.splice(index, 1);
      }
    }

    if (isDoubledLine && this.editDetail?.reviewPeriodType === this.reviewPeriodB?.reviewPeriodType) {
      this.serviceLineDetails = isDoubledLine ? this.filterLines(this.serviceLineDetails, this.editDetail.hcpcCode, this.editDetail?.reviewPeriodType) : this.serviceLineDetails;
    }

    this.selectedDetails = [...this.selectedDetails];
  }

  private buildServiceLineDetails(hcpcs: AuthorizationHCPCReqVisits[], retroDays: number) {
    hcpcs.forEach(({ hcpcCode, skillCode, totalVisits }) => {
      if (this.selectedDetails?.find(selectedDetail => selectedDetail?.hcpcCode === hcpcCode)) {

        this.selectedDetails.forEach(selectedDetail => {
          if (selectedDetail.hcpcCode === hcpcCode) {
            this.serviceLineDetails.push({
              ...selectedDetail,
              dateTimeFaxRecieved: this.dateTimeFaxReceived,
              ...(this.requestType === this.initialRequestType && { urgency: this.authRequest.authorizationDTO.urgency })
            });
          }
        });
      } else {
        this.serviceLineDetails.push(this.createServiceLineGridDetail(hcpcCode, skillCode, totalVisits, retroDays));
      }
    });
  }

  handleDetailRowUnChecked($event) {
    this.editDetail = $event.data;
    this.unCheckRowForNextPeriod();
    this.isLoading = true;
    this.clearOutCancelledServiceLine();
    setTimeout(() => this.isLoading = false, 0);
    this.serviceLinesChanged.emit(this.selectedDetails);
  }

  private findIndex(selectedDetails: ServiceLineGridDetail[], hcpcCode: string, reviewPeriodType: string): number {
    return selectedDetails
      .findIndex(selectedDetail => selectedDetail.hcpcCode === hcpcCode
        && (this.hh30DayReviewPeriods ? selectedDetail?.reviewPeriodType === reviewPeriodType : true))
  }

  handleEditDialogCancel() {
    this.isLoading = true;

    const index = this.findIndex(this.selectedDetails, this.editDetail.hcpcCode, this.editDetail?.reviewPeriodType);

    this.newLineForNextPeriod = null; // This is so that when we cancel out of the next period request dialog it doesn't create an unwanted duplicate of the next SL we create.
    this.clearOutCancelledServiceLine();
    this.editDetail = {};
    this.selectedDetails.splice(index, 1);
    this.selectedDetails = [...this.selectedDetails];
    setTimeout(() => this.isLoading = false, 0); // Refreshes the DOM so the grid will be updated

    this.serviceLinesChanged.emit(this.selectedDetails);
  }

  clearOutCancelledServiceLine() {
    const serviceLineDetail = this.findLine(this.serviceLineDetails, this.editDetail.hcpcCode, this.editDetail.reviewPeriodType);

    if (!serviceLineDetail) {
      return;
    }

    if (this.requestType === this.subsequentRequestType) {
      serviceLineDetail.urgency = null;
    }

    serviceLineDetail.dateLastVisitDelivered = null;
    serviceLineDetail.retroVisitsRequested = null;
    serviceLineDetail.timelyVisitsRequested = null;
    serviceLineDetail.retroDateRange = null;
    serviceLineDetail.totalVisitsRequested = null;
    serviceLineDetail.reviewPeriodType = null;
    serviceLineDetail.reviewPeriodEndDate = null;
    serviceLineDetail.reviewPeriodStartDate = null;
  }

  handleEditDialogSave(selectedMemberCriteriasIds: string[]) {
    this.isLoading = true;

    const index = this.findIndex(this.selectedDetails, this.editDetail.hcpcCode, this.editDetail?.reviewPeriodType);

    if (this.hh30DayReviewPeriods && this.newLineForNextPeriod) {
      const nextIndex = this.serviceLineDetails.findIndex(serviceLineDetail => serviceLineDetail.hcpcCode === this.editDetail.hcpcCode) + 1;

      this.serviceLineDetails.splice(nextIndex, 0, { ...this.editDetail });
    }

    this.selectedDetails[index] = { ...this.editDetail };
    this.selectedDetails[index].memberCriteriaIds = selectedMemberCriteriasIds;
    this.selectedDetails = [...this.selectedDetails];
    this.editDetail = {};
    this.newLineForNextPeriod = null;
    setTimeout(() => this.isLoading = false, 0); // Refreshes the DOM so the grid will be updated
    this.serviceLinesChanged.emit(this.selectedDetails);
  }

  getCertPeriodDateRange(effectiveFromDate: string): void {
    this.isLoading = true;
    this.authorizationRequestService.getCertPeriodRangeFromEffectiveFromDate(effectiveFromDate, this.dateTimeFaxReceived)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(result => {
        this.isLoading = false;
        if (result != null) {
          const fromDate = this.datePipe.transform(result.effectiveFromDate, "MM/dd/yyyy");
          const toDate = this.datePipe.transform(result.effectiveToDate, "MM/dd/yyyy");

          this.setAuthEffectiveDates(fromDate, toDate);
          this.setCertPeriodEffectiveDates(fromDate, toDate, result.reviewPeriods);
          this.setServiceLineNewDates(fromDate, toDate, fromDate);
        }
      });
  }

  setCertPeriodEffectiveDates(fromDate: string, toDate: string, reviewPeriods: ReviewPeriod[], isFutureCertPeriod = false) {
    const cpIndex = this.authRequest.authorizationCertPeriodDTOs.findIndex(e => e.certPeriod === this.selectedCertPeriod.certPeriod);
    this.authRequest.authorizationCertPeriodDTOs[cpIndex].effectiveFromDate = fromDate;
    this.authRequest.authorizationCertPeriodDTOs[cpIndex].recertDate = fromDate;
    this.authRequest.authorizationCertPeriodDTOs[cpIndex].effectiveToDate = toDate;
    this.authRequest.authorizationCertPeriodDTOs[cpIndex].modifiedBy = this.authenticationService.claims.emails[0];
    this.authRequest.authorizationCertPeriodDTOs[cpIndex].modifiedOn = moment().utc().toISOString();

    if (this.hh30DayReviewPeriods) {
      if (isFutureCertPeriod) {
        this.authRequest.authorizationCertPeriodDTOs[cpIndex].reviewPeriods = reviewPeriods;
      } else {
        this.authRequest.authorizationCertPeriodDTOs[cpIndex].reviewPeriods.forEach(r => {
          const reviewPeriod = reviewPeriods.find(x => x.reviewPeriodType == r.reviewPeriodType);

          r.startDate = reviewPeriod.startDate;
          r.endDate = reviewPeriod.endDate;
        });
      }
    }

    this.authRequestChanged.emit(this.authRequest);
    this.selectedCertPeriod = this.authRequest.authorizationCertPeriodDTOs[cpIndex];
  }

  setAuthEffectiveDates(fromDate: string, toDate: string) {
    this.authRequest.authorizationDTO.effectiveFromDate = fromDate;
    this.authRequest.authorizationDTO.recertDate = fromDate;
    this.authRequest.authorizationDTO.effectiveToDate = toDate;
    this.authRequest.authorizationDTO.modifiedBy = this.authenticationService.claims.emails[0];
    this.authRequest.authorizationDTO.modifiedOn = moment().utc().toISOString();
    this.authRequestChanged.emit(this.authRequest);
  }

  setServiceLineNewDates(newFromDate: string, newToDate: string, startOfCareDate?: string, contractStartDate?: string) {
    this.serviceLineDetails = this.serviceLineDetails.map(line => ({
      ...line,
      ...(startOfCareDate && { startOfCareDate }),
      ...(contractStartDate && { contractStartDate }),
      reCertDate: newFromDate,
      certFromDate: newFromDate,
      certToDate: newToDate,
    }));
  }

  setFormValidity() {
    this.isStepFormValid.emit(this.showPreview);  // Only let the user click the Next button if on Preview
  }

  createRetroactiveDateRangeCalc(): RetroactiveDateRangeCalc {
    const dateTimeFaxRecieved = moment.utc(this.dateTimeFaxReceived).local().format('L');
    const dateLastVisitDelivered = this.editDetail.dateLastVisitDelivered ?
      this.datePipe.transform(this.editDetail.dateLastVisitDelivered, "MM/dd/yyyy") :
      null;

    return {
      dateLastVisitDelivered,
      dateTimeFaxRecieved,
      requestType: this.requestType,
      certPeriodEffectiveToDate: this.selectedCertPeriod.effectiveToDate,
      startOfCareDate: this.authRequest.authorizationDTO.startOfCareDate,
      retroDays: this.editDetail.retroDays,
      ...(this.hasContractFlow && { contractStartDate: this.contractStartDate }),
      reviewPeriodStartDate: this.editDetail.reviewPeriodStartDate,
      reviewPeriodEndDate: this.editDetail.reviewPeriodEndDate
    };
  }

  /**
   * Update outdated selected lines
   */
  async updateSelectedLineDetails() {
    if (!this.selectedDetails) {
      return;
    }

    const hasChanges = this.selectedDetails
      .some(({ dateTimeFaxRecieved, dateLastVisitDelivered }) => (dateTimeFaxRecieved !== this.dateTimeFaxReceived) && dateLastVisitDelivered);

    this.updateUrgency();

    if (hasChanges) {
      const retroCalc = this.createRetroactiveDateRangeCalc();

      this.isLoading = false;
      this.authorizationRequestService
        .calculateRetroDateRanges(this.tenantId, retroCalc)
        .pipe(
          finalize(() => this.isLoading = false)
        )
        .subscribe(({ retroDateRange }) => {
          this.updateUrgency();
          this.selectedDetails = this.selectedDetails
            .map(selectedDetail => ({
              ...selectedDetail,
              dateTimeFaxRecieved: this.dateTimeFaxReceived,
              retroDateRange: selectedDetail.dateLastVisitDelivered ? retroDateRange : selectedDetail.retroDateRange
            }));

          if (this.serviceLineDetails.length) {
            this.authorizationHCPCReqVisits.forEach(({ hcpcCode }) => {
              const selectedIndex = this.selectedDetails.findIndex(e2 => e2.hcpcCode === hcpcCode);

              if (selectedIndex > -1) {
                this.serviceLineDetails.forEach((item, index) => {
                  if (item.hcpcCode === hcpcCode) {
                    this.serviceLineDetails[index] = { ...this.selectedDetails[selectedIndex] };
                  }
                });
              }
            });
          }

          const lines: ServiceLineGridDetail[] = this.loadNewServiceLineDetails();
          const updatedLines = lines.map(line => (line.dateLastVisitDelivered ? { ...line, retroDateRange } : { ...line }));
          this.serviceLinesChanged.emit(updatedLines);
        });
    }
  }

  updateUrgency() {
    if (this.selectedDetails.some(({ urgency }) => urgency !== this.authRequest.authorizationDTO.urgency)) {
      this.selectedDetails = this.selectedDetails.map(selectedDetail => ({
        ...selectedDetail,
        urgency: this.authRequest.authorizationDTO.urgency
      }));
    }
  }

  previewServiceLines() {
    this.showPreview = true;
    this.serviceLinesPreview = [];
    this.clearUnsavedAuthDetails();
    const slgd = new ServiceLinesGridDetails();
    slgd.requestType = this.requestType;
    slgd.existingServiceLineDetails = this.loadExistingServiceLineDetails();
    slgd.newServiceLineDetails = this.loadNewServiceLineDetails();

    this.getServiceLinesPreview(slgd);
  }

  loadNewServiceLineDetails(): ServiceLineGridDetail[] {
    return this.selectedDetails.reduce<ServiceLineGridDetail[]>((slgd, detail) => {
      const detailData = { ...detail };
      // convert dateTimeFaxReceived to local time for correct calculation Service Lines Preview data
      // and remove timezone offset form dateTimeFaxRecieved
      const dateTimeFaxReceived = moment.utc(this.dateTimeFaxReceived).local().format().slice(0, -6);
      detailData.dateLastVisitDelivered = detail.dateLastVisitDelivered
        ? this.datePipe.transform(detail.dateLastVisitDelivered, "MM/dd/yyyy")
        : null;
      // remove timezone offset form dateTimeFaxRecieved
      detailData.dateTimeFaxRecieved = dateTimeFaxReceived;
      detailData.startOfCareDate = moment(detail.startOfCareDate).format('L');
      detailData.reCertDate = detail.reCertDate === null ? null : this.datePipe.transform(detail.reCertDate, "MM/dd/yyyy");
      detailData.retroVisitsRequested = detail.retroVisitsRequested ? detail.retroVisitsRequested : 0;
      detailData.timelyVisitsRequested = detail.timelyVisitsRequested ? detail.timelyVisitsRequested : 0;
      detailData.additionalComments=detail.additionalComments?detail.additionalComments:null;

      // if we have certPeriod as an Input it means that we selected future cert period in new benefit plan workflow
      // current cert period - 1
      // future cert period - 2
      if (this.certPeriod) {
        detailData.certPeriod = 2;
      }

      slgd.push(detailData);

      return slgd;
    }, []);
  }

  loadExistingServiceLineDetails(): ExistingServiceLineDetail[] {
    const esld: ExistingServiceLineDetail[] = [];

    this.authRequest.authorizationDetailDTOs.forEach(detail => {
      if (detail.hcpccode !== this.zzHcpc) {
        const esl = new ExistingServiceLineDetail();
        // convert dateTimeFaxReceived to local time for correct calculation Service Lines Preview data
        // and remove timezone offset form dateTimeFaxRecieved
        const dateTimeFaxReceived = moment.utc(detail.dateTimeFaxRecieved).local().format().slice(0, -6);

        esl.authorizationDetailId = detail.authorizationDetailId;
        // remove timezone offset form dateTimeFaxRecieved
        esl.dateTimeFaxRecieved = dateTimeFaxReceived;
        esl.dateLastVisitDelivered = detail.dateLastVisitDelivered;
        esl.serviceStatus = detail.serviceStatus;
        esl.requestedAmount = detail.requestedAmount;
        esl.effectiveFromDate = detail.effectiveFromDate;
        esl.effectiveToDate = detail.effectiveToDate;
        esl.requestType = detail.requestType;
        esl.skillCode = detail.skillCode;
        esl.hcpcCode = detail.hcpccode;
        esl.urgency = detail.urgency;
        esl.startOfCareDate = this.authRequest.authorizationDTO.startOfCareDate;
        esl.certPeriod = detail.certPeriod;
        esl.authorizationId = detail.authorizationId
        esl.additionalComments = detail.additionalComments
        if (this.hh30DayReviewPeriods) {
          const certPeriod = this.authRequest.authorizationCertPeriodDTOs.find(c => c.certPeriod == detail.certPeriod);
          const reviewPeriod = certPeriod.reviewPeriods.find(x => x.reviewPeriodType === detail.reviewPeriodType);

          this.selectedReviewPeriodEndDate = reviewPeriod.endDate;
          this.selectedReviewPeriodStartDate = reviewPeriod.startDate;
          esl.reviewPeriodType = detail.reviewPeriodType;
          esl.reviewPeriodStartDate = reviewPeriod.startDate;
          esl.reviewPeriodEndDate = reviewPeriod.endDate;
          esl.certToDate = certPeriod.effectiveToDate;
          esl.certFromDate = certPeriod.effectiveFromDate;
        }

        esld.push(esl);
      }
    });

    return esld;
  }

  getServiceLinesPreview(slgd: ServiceLinesGridDetails) {
    this.isLoading = true;
    const certDateFormat = "MM/dd/yyyy";
    this.authorizationRequestService.calculateServiceLinesPreview(this.tenantId, slgd)
      .subscribe(resp => {
        resp.forEach(e => {
          if (e.effectiveFromDate.endsWith('Z')) { e.effectiveFromDate = e.effectiveFromDate.slice(0, -1); }
          if (e.effectiveToDate.endsWith('Z')) { e.effectiveToDate = e.effectiveToDate.slice(0, -1); }
          e.effectiveFromDate = this.datePipe.transform(e.effectiveFromDate, certDateFormat);
          e.effectiveToDate = this.datePipe.transform(e.effectiveToDate, certDateFormat);
        });
        this.serviceLinesPreview = resp;
        this.updateAuthDetailsEffectiveDates(resp);
        this.isLoading = false;
        this.setFormValidity();
        this.createAuthServiceLines();
      });
  }

  updateAuthDetailsEffectiveDates(slps: ServiceLineGridPreview[]) {
    slps.filter(e => !e.isNew).forEach(detail => {
      const ind = this.authRequest.authorizationDetailDTOs.findIndex(e => e.authorizationDetailId === detail.authorizationDetailId);
      if (ind >= 0) {
        if (detail.urgency === this.retroServiceType) {
          this.authRequest.authorizationDetailDTOs[ind].urgency = this.routineUrgency;
        }
        const efffrDt = this.datePipe.transform(this.authRequest.authorizationDetailDTOs[ind].effectiveFromDate, "MM/dd/yyyy");
        const efftoDt = this.datePipe.transform(this.authRequest.authorizationDetailDTOs[ind].effectiveToDate, "MM/dd/yyyy");
        // only if eff dates modified 16933
        if (efffrDt !== detail.effectiveFromDate || efftoDt !== detail.effectiveToDate) {
          this.authRequest.authorizationDetailDTOs[ind].effectiveFromDate = detail.effectiveFromDate;
          this.authRequest.authorizationDetailDTOs[ind].effectiveToDate = detail.effectiveToDate;
          this.authRequest.authorizationDetailDTOs[ind].modifiedBy = this.authenticationService.claims.emails[0];
          this.authRequest.authorizationDetailDTOs[ind].modifiedOn = moment().utc().toISOString();
        }
        this.authRequest.authorizationDetailDTOs[ind].additionalComments = detail.additionalComments;
        this.authRequestChanged.emit(this.authRequest);
      }
    });
  }

  returnToServiceLinesDetails() {
    this.authRequest.authorizationDetailDTOs.forEach(detail => {
      const authorizationDetailDTO = this.originalAuthorizationDetailDTOs
        .find(originalDetail => originalDetail.authorizationDetailId === detail.authorizationDetailId);

      if (authorizationDetailDTO) {
        detail.effectiveFromDate = authorizationDetailDTO.effectiveFromDate;
        detail.effectiveToDate = authorizationDetailDTO.effectiveToDate;
        detail.requestSource = authorizationDetailDTO.requestSource;
      }
    });

    this.showPreview = false;
    this.setFormValidity();
  }

  showInitialQuestions() {
    if (this.selectedDetails.length === 0) {
      this.displayServiceLineQuestion = true;
    } else {
      this.socDateEntered = this.authRequest.authorizationDTO.startOfCareDate;
    }
  }

  handleHasInfoForServiceLines(hasInfo: boolean) {
    this.displayServiceLineQuestion = false;

    if (hasInfo) {

      if (this.requestType === 'Initial') {

        // For contract flow we need to choose cert period first and only after that we should select hipps
        // Ask for HIPPS only in initial request process
        // Subsequent process handle this process in his own component
        this.hasContractFlow = this.checkContractStartDate();

        const startOfCareDate = this.authRequest.authorizationDTO?.startOfCareDate;

        if (this.hasContractFlow) {
          this.getCertPeriodRangeForContractStartDate();
        } else if (!this.selectedCertPeriod.hippscode) {
          this.displayHippsCodeDialog = true;
        }
        else {
          this.getCertPeriodDateRange(this.datePipe.transform(startOfCareDate, 'MM-dd-yyyy'));
        }
      }
    } else {
      if (this.isExternalUser) {
        this.messageService.add({
          severity: 'error', summary: 'Service Line Information Required',
          detail: `You must have service lines to complete your authorization request.`,
          life: 5000
        });
        setTimeout(() => {
          this.navigateToMemberDetails();
        }, 6000);
      } else {
        this.createZzServiceLine();
        setTimeout(() => this.goToNextStep.emit(true), 0);
      }
    }
  }

  setOverrideReason(requestType: string, certPeriod: number): string[] {
    if (this.authRequest.authorizationDTO.secondaryClinicalGrouping === 'Long Term/Chronic'
      && this.serviceProvider?.providerPgpDTO?.reimbursementType === 'Per Visit') {

      if (requestType === this.retroServiceType && certPeriod <= 2) {
        return;
      }

      return ['Chronic'];
    }

    return;
  }

  createAuthServiceLines() {
    this.clearUnsavedAuthDetails();
    const groupId: number = this.getMaxGroupId(this.authRequest.authorizationDetailDTOs);
    const ads: AuthorizationDetail[] = [];
    this.serviceLinesPreview.filter(e => e.isNew).forEach(slPreview => {
      const ad = new AuthorizationDetail();
      const hcpc = this.authorizationHCPCReqVisits.find(e => e.hcpcCode === slPreview.hcpcCode);
      ad.authorizationDetailId = this.emptyGuid;
      ad.authorizationId = this.authRequest.authorizationDTO.authorizationId;
      ad.authorizationNumber = this.authRequest.authorizationDTO.authorizationNumber;
      ad.skillCode = slPreview.skillCode;
      ad.hcpccode = hcpc?.hcpcCode;
      ad.hcpcid = hcpc?.hcpcId;
      ad.revenueCodeId = hcpc?.revenueCodeId;
      ad.requestedAmount = slPreview.visits;
      ad.requestType = slPreview.serviceType;
      ad.urgency = slPreview.urgency;
      ad.revenueCode = hcpc?.revenueCode;
      ad.dateTimeFaxRecieved = this.dateTimeFaxReceived;
      ad.dateLastVisitDelivered = slPreview.dateLastVisitDelivered;
      ad.certPeriod = slPreview.certPeriod;
      ad.effectiveFromDate = slPreview.effectiveFromDate;
      ad.effectiveToDate = slPreview.effectiveToDate;
      ad.serviceStatus = 'Incomplete';
      ad.additionalComments = slPreview.additionalComments;
      ad.serviceType = this.homeHealth;
      ad.extension = false;
      ad.letterMailed = false;
      ad.qualityCheck = false;
      ad.wdaexception = false;
      ad.urgencyChange = false;
      ad.letterReviewed = false;
      ad.urgencyDowngraded = false;
      ad.createdBy = this.authenticationService.claims.emails[0];
      if (this.requestType === this.initialRequestType) {
        ad.serviceLineGroup = groupId;
        ad.requestor = this.authRequest.authorizationDTO.requestorName;
        ad.requestSource = this.isExternalUser ? RequestSources.External : this.authRequest.authorizationDTO.requestSource;
        ad.memberRepresentative = this.memberRepresentativeName || null;
      } else {
        ad.serviceLineGroup = groupId + 1;
        ad.requestor = this.isExternalUser
          ? this.setRequestorByUserRole()
          : this.authRequest.authorizationDTO.requestorName;
        ad.requestSource = this.isExternalUser ? RequestSources.External : this.authRequest.authorizationDTO.requestSource;
        ad.memberRepresentative = this.memberRepresentativeName;
      }
      ad.overrideReason = this.setOverrideReason(ad.requestType, ad.certPeriod);
      ad.reviewPeriodType = slPreview.reviewPeriodType;
      ads.push(ad);
    });
    if (this.requestType === this.initialRequestType) {
      this.authRequest.authorizationDetailDTOs.length = 0;
      this.authRequest.authorizationDetailDTOs = ads;
    } else {
      ads.forEach(r => { this.authRequest.authorizationDetailDTOs.push(r); });
    }
    this.authRequestChanged.emit(this.authRequest);
  }

  createZzServiceLine() {
    this.clearUnsavedAuthDetails();
    let groupId = 1;
    const ad = new AuthorizationDetail();
    ad.authorizationDetailId = this.emptyGuid;
    ad.authorizationId = this.authRequest.authorizationDTO.authorizationId;
    ad.authorizationNumber = this.authRequest.authorizationDTO.authorizationNumber;
    ad.urgency = this.authRequest.authorizationDTO.urgency;
    ad.hcpccode = this.zzHcpc;
    ad.dateTimeFaxRecieved = this.dateTimeFaxReceived;
    ad.certPeriod = this.selectedCertPeriod.certPeriod;
    ad.effectiveFromDate = this.selectedCertPeriod.effectiveFromDate;
    ad.effectiveToDate = this.selectedCertPeriod.effectiveToDate;
    ad.serviceStatus = 'Incomplete';
    ad.serviceType = this.homeHealth;
    ad.extension = false;
    ad.letterMailed = false;
    ad.qualityCheck = false;
    ad.wdaexception = false;
    ad.urgencyChange = false;
    ad.letterReviewed = false;
    ad.urgencyDowngraded = false;
    ad.createdBy = this.authenticationService.claims.emails[0];
    //Missed field US 56725 as reported by bug 125241
    //ZZ create only Internal user
    ad.requestSource = this.authRequest.authorizationDTO.requestSource;

    if (this.requestType === this.initialRequestType) {
      ad.serviceLineGroup = groupId;
      this.authRequest.authorizationDetailDTOs.length = 0;
      this.authRequest.authorizationDetailDTOs.push(ad);
    } else {
      groupId = this.getMaxGroupId(this.authRequest.authorizationDetailDTOs);
      ad.serviceLineGroup = groupId + 1;
      this.authRequest.authorizationDetailDTOs.push(ad);
    }

    ad.overrideReason = this.setOverrideReason(ad.serviceType, ad.certPeriod);

    if (this.hh30DayReviewPeriods) {
      ad.reviewPeriodType = ReviewPeriodType.ReviewPeriodA
    }

    this.authRequestChanged.emit(this.authRequest);
  }

  clearUnsavedAuthDetails() {
    this.authRequest.authorizationDetailDTOs = this.authRequest.authorizationDetailDTOs.filter(e => e.authorizationDetailId !== this.emptyGuid);
  }

  private setRequestorByUserRole(): string {
    if (this.serviceProviderBusinessGroup.includes(this.businessType.businessTypeID)) {
      return 'HHA';
    }

    if (this.physicianBusinessGroup.includes(this.businessType.businessTypeID)) {
      return 'MD';
    }

    if (this.healthPlanCaseManagerBusinessGroup.includes(this.businessType.businessTypeID)) {
      return 'Health Plan Case Manager';
    }

    return null;
  }

  private getBusinessType() {
    this.tenantService.getUserBusinessType(this.isExternalUser)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((businessType: BusinessType) => this.businessType = businessType);
  }


  getMaxGroupId(sls: AuthorizationDetail[]): number {
    let groupId = 1;

    if (sls && sls.length > 0) {
      sls.forEach(r => {
        if (r.serviceLineGroup > groupId) { groupId = r.serviceLineGroup; }
      });
    }

    return groupId;
  }

  navigateToMemberDetails() {
    this.router.navigate([this.tenantId, 'member', this.subscriberNumber]);
  }

  handleHippsDialogContinue() {
    this.displayHippsCodeDialog = false;
  }

  handleCertPeriodSelected({ effectiveFromDate, effectiveToDate, reviewPeriods }: CertPeriodRangeCalcDTO) {
    const contractStartDate = this.contractStartDate;

    if (this.hh30DayReviewPeriods) {
      this.reviewPeriods = reviewPeriods;
      this.manageReviewPeriodOptions();
      this.canRequestNextReviewPeriod = !!this.reviewPeriodB && (!this.isDTRRMoreThan7DaysToReviewPeriodB || this.reviewPeriodException);
      this.noRetroVisitsForCSD = this.isHHRequestProcessUpdates && new Date(this.certPeriods[0].effectiveFromDate).getTime() !== new Date(effectiveFromDate).getTime();
    }

    if (this.requestType === 'Initial') {
      const [currentCertPeriod, futureCertPeriod] = this.certPeriods;
      let isFutureCertPeriod = false;

      /**
       * Sets the CURRENT cert period dates and review periods to the cert period two.
       * This is used as a placeholder for the future date until we call the "setCertPeriodQueue" method in the auth-request-intial component
       */
      if (effectiveFromDate === futureCertPeriod.effectiveFromDate) {
        isFutureCertPeriod = true;
        const newCertPeriod = new AuthorizationCertPeriod();
        newCertPeriod.authorizationId = this.authRequest.authorizationDTO.authorizationId;
        newCertPeriod.certPeriod = 2;
        newCertPeriod.recertDate = this.datePipe.transform(currentCertPeriod.effectiveFromDate, "MM/dd/yyyy");
        newCertPeriod.effectiveFromDate = this.datePipe.transform(currentCertPeriod.effectiveFromDate, "MM/dd/yyyy");
        newCertPeriod.effectiveToDate = this.datePipe.transform(currentCertPeriod.effectiveToDate, "MM/dd/yyyy");
        newCertPeriod.createdBy = this.authenticationService.claims.emails[0];
        newCertPeriod.modifiedBy = this.authenticationService.claims.emails[0];
        newCertPeriod.createdOn = moment().utc().toISOString();
        newCertPeriod.modifiedOn = moment().utc().toISOString();
        newCertPeriod.isLocked = false;
        newCertPeriod.reviewPeriods = currentCertPeriod.reviewPeriods;
        this.certPeriodQueue.emit(newCertPeriod);
      } else {
        this.certPeriodQueue.emit(null);
      }

      // Update current cert period effective dates
      // Future cert period will be automatically created with latest values
      this.setCertPeriodEffectiveDates(effectiveFromDate, effectiveToDate, reviewPeriods, isFutureCertPeriod);
      this.setAuthEffectiveDates(effectiveFromDate, effectiveToDate);
      this.setServiceLineNewDates(effectiveFromDate, effectiveToDate, this.authRequest.authorizationDTO.startOfCareDate, contractStartDate);

      this.displayHippsCodeDialog = true;
    } else {
      this.setAuthEffectiveDates(effectiveFromDate, effectiveToDate);
      this.setServiceLineNewDates(effectiveFromDate, effectiveToDate, this.authRequest.authorizationDTO.startOfCareDate, contractStartDate);
    }
  }

  /**
   * - If the ContractStartDate is null for the Member's active BenefitPlan, the Initial Request workflow continues as normal.
   * - If the ContractStartDate is NOT null for the Member's active BenefitPlan and the SOC date entered by the user is greater than or equal
   * to the ContractStartDate, the Initial Request workflow continues as normal.
   * - If the ContractStartDate is NOT null for the Member's active BenefitPlan and the SOC date entered by the user is less than the
   * ContractStartDate, the Initial Request process will use the new workflow branch.
   * @private
   */
  private checkContractStartDate(): boolean {
    const contractStartDate = this.contractStartDate;
    const startOfCareDate = moment(this.authRequest.authorizationDTO.startOfCareDate);
    return !!contractStartDate && startOfCareDate.isBefore(contractStartDate);
  }

  private getCertPeriodRangeForContractStartDate() {
    this.authorizationService.getCertPeriodRangeForContractStartDate(this.contractStartDate, this.authRequest.authorizationDTO.startOfCareDate, this.dateTimeFaxReceived)
      .subscribe((certPeriods) => {
        if (certPeriods) {
          this.certPeriods = certPeriods;
          this.certPeriodModalVisible = true;

          if (certPeriods.length > 1) { //addressing scenario with multiple cert periods
            const firstCertPeriod = certPeriods.reduce((prev, curr) => (new Date(prev.effectiveFromDate) < new Date(curr.effectiveFromDate) ? prev : curr));
            const startDateReviewPeriodB = new Date(firstCertPeriod.reviewPeriods?.find(x => x.reviewPeriodType == "ReviewPeriodB")?.startDate);
            const dateTimeFaxReceived = new Date(this.dateTimeFaxReceived);
            const startDateReviewPeriodBMinus7 = new Date(startDateReviewPeriodB);
            startDateReviewPeriodBMinus7.setDate(startDateReviewPeriodBMinus7.getDate() - 7);
            //auto select cert period 1 if fax received date is not within 7 days of review period B start date
            if(this.isDTRRMoreThan7DaysToReviewPeriodB){
              this.certPeriodModalVisible = false;
              this.handleCertPeriodSelected(firstCertPeriod);
            }
          }

        }
      });
  }
}
